import { useState } from "react";

const C = {
  bg:         '#020617',
  surface:    'rgba(255,255,255,0.05)',
  surface2:   'rgba(255,255,255,0.08)',
  border:     'rgba(255,255,255,0.10)',
  borderHov:  'rgba(255,255,255,0.20)',
  accent:     '#60A5FA',
  accentDim:  '#3B82F6',
  accentGlow: 'rgba(96,165,250,0.15)',
  gold:       '#F0A500',
  green:      '#34D399',
  red:        '#F87171',
  text:       '#FFFFFF',
  textMuted:  '#94A3B8',
  textFaint:  '#475569',
};

const F = {
  display: "'Playfair Display', Georgia, serif",
  body:    "'DM Sans', 'Segoe UI', sans-serif",
};

const faqs = [
  {
    category: "Getting Started",
    icon: "🚀",
    color: C.accent,
    questions: [
      {
        q: "What is SkillBite?",
        a: "SkillBite is an AI-powered micro-skills learning platform that teaches practical, high-ROI digital skills in 5–15 minute modules. Think MasterClass quality meets Duolingo's format — short, focused, and personalized to your goals. Every course is designed to get you productive fast, not just educated."
      },
      {
        q: "How is SkillBite different from Udemy or Coursera?",
        a: "Three key differences: (1) Modules are 5–15 minutes, not multi-hour lectures — designed for busy people. (2) Our AI generates a personalized learning path based on your goals, time, and skill level — not a one-size-fits-all curriculum. (3) Every course is built around practical outcomes, not just theory. You'll build something real by the end."
      },
      {
        q: "How does the AI Skill Path Generator work?",
        a: "Answer 4 quick questions about your goal, daily time, skill level, and area of interest. Our AI — powered by Claude by Anthropic — analyzes your answers against all available courses and builds you a personalized 3-course path in order. Each recommendation comes with a specific reason why it fits your goal. You can regenerate a new path at any time."
      },
      {
        q: "Do I need to create an account to use SkillBite?",
        a: "You can browse courses and use the AI Skill Path Generator without an account. However, to enroll in courses, track your progress, or make purchases, you'll need to sign in. We use Clerk for authentication — you can sign in instantly with Google or any email address."
      },
      {
        q: "What devices can I use SkillBite on?",
        a: "SkillBite works on any device with a web browser — desktop, tablet, and mobile. The platform is fully responsive and optimized for phone screens. A dedicated mobile app for iOS and Android is on our roadmap."
      },
    ]
  },
  {
    category: "Courses & Learning",
    icon: "🎓",
    color: C.gold,
    questions: [
      {
        q: "What topics does SkillBite cover?",
        a: "Our current library covers: AI Workflows, No-Code Automation, Short Video & TikTok, Solopreneur Business, Graphic Design & Branding, and Productivity. We add new courses every month based on what skills are most in-demand. Courses are available globally and include region-specific tracks for Nigeria, India, and the US."
      },
      {
        q: "How long does it take to complete a course?",
        a: "Individual modules are 5–15 minutes. Full courses range from 38 minutes to 2.4 hours depending on depth. Career Packs — our bundled 30-day programs — are designed for 10–30 minutes per day over 30 days. You learn at your own pace with no deadlines."
      },
      {
        q: "What are Career Packs?",
        a: "Career Packs are curated 30-day transformation programs that bundle multiple courses into a structured learning track. Examples: 'Become an AI Video Creator', 'Start a WhatsApp Chatbot Business', 'AI Solopreneur in a Box'. Each pack is designed to take you from zero to a functional skill or income stream in 30 days."
      },
      {
        q: "Do I get a certificate when I complete a course?",
        a: "Yes — Pro plan subscribers receive a verifiable completion certificate for every course they finish. Certificates show the course title, completion date, and your name. They can be shared directly to LinkedIn or downloaded as PDF. Basic plan users can earn certificates by upgrading to Pro."
      },
      {
        q: "Can I download courses to watch offline?",
        a: "Offline downloading is not currently available. All courses stream from our platform. We recommend watching on a strong Wi-Fi connection for the best experience. Offline access is on our product roadmap for the mobile app."
      },
      {
        q: "What happens to my progress if I cancel my subscription?",
        a: "Your account and progress are preserved indefinitely. If you cancel a paid plan, you'll lose access to premium courses at the end of your billing period, but your completed courses, certificates, and profile remain. You can resubscribe at any time to regain access."
      },
    ]
  },
  {
    category: "Pricing & Billing",
    icon: "💳",
    color: C.green,
    questions: [
      {
        q: "How much does SkillBite cost?",
        a: "SkillBite offers three tiers: Free (limited access, no card required), Basic at $9/month or $7/month billed annually, and Pro at $19/month or $15/month billed annually. Annual plans save you 20%. You can also purchase individual courses and Career Packs as one-time payments without a subscription."
      },
      {
        q: "What's included in the Pro plan vs Basic?",
        a: "Basic gives you access to 50+ free courses, the AI Skill Path Generator, and community access. Pro includes everything in Basic plus unlimited premium courses, all Career Packs, 20% off coaching sessions, the Creator Dashboard, verifiable certificates of completion, and priority support."
      },
      {
        q: "Can I try Pro before paying?",
        a: "The Free plan lets you explore the platform fully — browse all courses, use the AI Skill Path Generator, and enroll in any free course — before committing to a paid plan. We don't currently offer a free trial of Pro, but you can cancel within the first month if you're not satisfied."
      },
      {
        q: "How do I cancel my subscription?",
        a: "You can cancel anytime from Account Settings → Subscription → Cancel Plan. Cancellation takes effect at the end of your current billing period — you keep full access until then. No questions asked, no cancellation fees."
      },
      {
        q: "Do you offer refunds?",
        a: "Subscriptions are non-refundable once a billing period has started. For one-time course purchases, we offer a full refund within 7 days if you've completed less than 20% of the course. Marketplace template and coaching purchases are non-refundable once delivered. Email admin@skillbite.ai to request a refund."
      },
      {
        q: "Do you offer discounts for students or teams?",
        a: "Corporate team packages are available starting at $499/year for groups of 5+, with custom pricing for larger teams. Student discounts are not currently available but are planned. If you represent an educational institution, email us at admin@skillbite.ai to discuss options."
      },
    ]
  },
  {
    category: "Marketplace & Creators",
    icon: "🏪",
    color: "#9C6EFF",
    questions: [
      {
        q: "How does the SkillBite marketplace work?",
        a: "The marketplace is where independent experts sell courses, templates, SOPs, and 1-on-1 coaching sessions directly to learners. You can browse by category, filter by price, and purchase items instantly. Templates are delivered immediately after purchase. Coaching sessions are scheduled directly with the creator."
      },
      {
        q: "How do I become a creator on SkillBite?",
        a: "Go to Creator Studio and fill out the creator application. We review applications within 5 business days. Approved creators get access to the full Creator Dashboard — course upload, analytics, earnings tracking, and payout management. We prioritize creators with practical expertise in high-demand skill areas."
      },
      {
        q: "How much do creators earn?",
        a: "Creators keep 65–80% of net revenue depending on their creator tier. SkillBite retains 20–35% as a platform commission. Payouts are processed monthly via Stripe with a minimum threshold of $25. Creators are responsible for their own tax obligations. Over $4.2M has been paid out to creators to date."
      },
      {
        q: "What types of products can I sell as a creator?",
        a: "Creators can sell: short courses (video-based, 5–60 minutes), templates (prompt packs, SOPs, Canva kits, email sequences), and coaching sessions (1-on-1 video calls booked and paid through the platform). All products must be original, accurate, and deliver on their described outcomes."
      },
      {
        q: "What quality standards do courses need to meet?",
        a: "Courses must have clear audio, a structured curriculum with a defined outcome, and deliver practical skills. We review all submissions before publishing. Our team may request revisions if quality standards aren't met. Courses with consistently poor ratings (below 3.5 stars) may be delisted."
      },
    ]
  },
  {
    category: "Account & Privacy",
    icon: "🔐",
    color: C.red,
    questions: [
      {
        q: "How do I delete my account?",
        a: "Go to Account Settings → Delete Account. This permanently deletes your profile, progress, enrollments, and all associated data within 30 days. Completed certificates and purchase receipts are retained for 90 days for legal and financial compliance before deletion. This action cannot be undone."
      },
      {
        q: "Does SkillBite sell my data?",
        a: "No — we never sell your personal data to third parties. Your data is used solely to operate the platform, personalize your learning experience, and send you transactional emails. We use privacy-respecting analytics (Vercel Analytics) that don't require cookie consent banners."
      },
      {
        q: "How does SkillBite use my data to personalize learning?",
        a: "Your skill path answers are sent to Claude AI by Anthropic to generate course recommendations. Your enrollment history and completion data help us surface relevant next courses. We do not use your data to train AI models. See our Privacy Policy for full details."
      },
      {
        q: "Is my payment information secure?",
        a: "Yes. Payments are processed exclusively by Stripe, which is PCI DSS Level 1 certified — the highest standard for payment security. SkillBite never sees or stores your full card details. Stripe handles all card data on their secure infrastructure."
      },
      {
        q: "I forgot my password. How do I reset it?",
        a: "On the sign-in screen, click 'Forgot password?' and enter your email. Clerk will send a reset link to your inbox. If you signed up with Google, you don't have a password — just click 'Continue with Google' on the sign-in screen."
      },
    ]
  },
  {
    category: "Technical Support",
    icon: "⚙️",
    color: C.accentDim,
    questions: [
      {
        q: "A video isn't loading. What should I do?",
        a: "Try these steps in order: (1) Refresh the page. (2) Check your internet connection — video streaming requires at least 5 Mbps. (3) Try a different browser (Chrome recommended). (4) Clear your browser cache. If the problem persists, email admin@skillbite.ai with the course name and your browser version."
      },
      {
        q: "The AI Skill Path Generator gave me an error. What happened?",
        a: "The AI generator occasionally experiences timeouts during high traffic periods. Please wait 30 seconds and try again. If the error persists, it may be a temporary issue with the Anthropic API. You can check status at status.anthropic.com. Email us if the problem continues for more than 10 minutes."
      },
      {
        q: "My payment was charged but I can't access my course. Help!",
        a: "First, try refreshing the page and logging out then back in. If the issue persists, check that you're logged in with the same account you used to purchase. Email admin@skillbite.ai with your purchase receipt and we'll resolve it within 24 hours. You will never lose access to something you've paid for."
      },
      {
        q: "I didn't receive my welcome email. What should I do?",
        a: "Check your Spam or Junk folder first — emails from hello@skillbite.ai occasionally end up there. If you use Gmail, also check the Promotions tab. Add admin@skillbite.ai to your contacts to ensure future emails arrive. If you still can't find it after 5 minutes, email us at admin@skillbite.ai."
      },
      {
        q: "How do I contact support?",
        a: "Email us at admin@skillbite.ai — we respond within 24 business hours. Pro plan subscribers get priority support with faster response times. For urgent payment issues, include 'URGENT' in your subject line and your order/subscription ID."
      },
    ]
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        borderBottom: `1px solid ${C.border}`,
        transition: "all 0.2s",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "18px 0",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          gap: 16,
          font: "inherit",
        }}
      >
        <span style={{
          fontSize: 14,
          fontWeight: 600,
          color: open ? C.accent : C.text,
          lineHeight: 1.5,
          transition: "color 0.2s",
          fontFamily: F.body,
        }}>
          {q}
        </span>
        <span style={{
          fontSize: 18,
          color: open ? C.accent : C.textMuted,
          transition: "all 0.3s",
          transform: open ? "rotate(45deg)" : "rotate(0deg)",
          flexShrink: 0,
          fontWeight: 300,
          lineHeight: 1,
        }}>+</span>
      </button>
      {open && (
        <div style={{
          paddingBottom: 18,
          fontSize: 13,
          color: C.textMuted,
          lineHeight: 1.85,
          animation: "fadeUp 0.2s ease",
        }}>
          {a}
        </div>
      )}
    </div>
  );
}

function CategoryPill({ icon, label, color, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "8px 16px",
        borderRadius: 50,
        border: `1px solid ${active ? color : C.border}`,
        background: active ? color + "18" : "transparent",
        color: active ? color : C.textMuted,
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        cursor: "pointer",
        transition: "all 0.2s",
        fontFamily: F.body,
        whiteSpace: "nowrap",
      }}
      onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = color + "60"; e.currentTarget.style.color = color; } }}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; } }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span>
      {label}
    </button>
  );
}

export function FAQPage({ setPage }) {
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = faqs
    .filter(cat => activeCategory === "All" || cat.category === activeCategory)
    .map(cat => ({
      ...cat,
      questions: cat.questions.filter(
        item =>
          search === "" ||
          item.q.toLowerCase().includes(search.toLowerCase()) ||
          item.a.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter(cat => cat.questions.length > 0);

  const totalQ = faqs.reduce((sum, cat) => sum + cat.questions.length, 0);

  return (
    <div className="fade-in" style={{ maxWidth: 860, margin: "0 auto", padding: "48px 24px 100px" }}>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 52 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 12 }}>
          Help Center
        </div>
        <h1 style={{ fontFamily: F.display, fontSize: "clamp(28px,4vw,48px)", fontWeight: 900, marginBottom: 14, lineHeight: 1.15 }}>
          Frequently Asked<br />
          <span style={{ color: C.accent }}>Questions</span>
        </h1>
        <p style={{ color: C.textMuted, fontSize: 14, maxWidth: 480, margin: "0 auto 28px", lineHeight: 1.75 }}>
          Everything you need to know about SkillBite. Can't find your answer?
          Email us at <a href="mailto:admin@skillbite.ai" style={{ color: C.accent, textDecoration: "none" }}>admin@skillbite.ai</a>
        </p>

        {/* Search */}
        <div style={{ position: "relative", maxWidth: 440, margin: "0 auto" }}>
          <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 15, color: C.textMuted, pointerEvents: "none" }}>🔍</span>
          <input
            className="input-field"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search questions..."
            style={{ paddingLeft: 42, borderRadius: 50, fontSize: 14 }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 16 }}
            >✕</button>
          )}
        </div>
      </div>

      {/* Stats strip */}
      <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 36, flexWrap: "wrap" }}>
        {[
          { n: totalQ + "+", l: "Questions answered" },
          { n: "24hr", l: "Support response time" },
          { n: "4.8★", l: "Support satisfaction" },
        ].map(s => (
          <div key={s.l} style={{ padding: "8px 20px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, textAlign: "center" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.accent, fontFamily: F.display }}>{s.n}</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Category pills */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 40, justifyContent: "center" }}>
        <CategoryPill icon="🌐" label="All Topics" color={C.accent} active={activeCategory === "All"} onClick={() => setActiveCategory("All")} />
        {faqs.map(cat => (
          <CategoryPill key={cat.category} icon={cat.icon} label={cat.category} color={cat.color} active={activeCategory === cat.category} onClick={() => setActiveCategory(cat.category)} />
        ))}
      </div>

      {/* No results */}
      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: C.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>No results found</div>
          <div style={{ fontSize: 13 }}>Try a different search term or browse all categories</div>
          <button className="btn-secondary" style={{ marginTop: 20 }} onClick={() => { setSearch(""); setActiveCategory("All"); }}>
            Clear filters
          </button>
        </div>
      )}

      {/* FAQ Sections */}
      <div style={{ display: "flex", flexDirection: "column", gap: 48 }}>
        {filtered.map((cat) => (
          <div key={cat.category}>
            {/* Category header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: `2px solid ${cat.color}30` }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: cat.color + "18", border: `1px solid ${cat.color}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                {cat.icon}
              </div>
              <div>
                <h2 style={{ fontFamily: F.display, fontSize: 20, fontWeight: 800, color: cat.color, lineHeight: 1 }}>{cat.category}</h2>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{cat.questions.length} question{cat.questions.length !== 1 ? "s" : ""}</div>
              </div>
            </div>

            {/* Questions */}
            <div>
              {cat.questions.map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Still need help CTA */}
      {filtered.length > 0 && (
        <div style={{ marginTop: 64, background: `linear-gradient(135deg,${C.surface},rgba(0,52,89,0.4))`, border: `1px solid ${C.accent}20`, borderRadius: 20, padding: "36px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🙋</div>
          <h3 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Still have questions?</h3>
          <p style={{ color: C.textMuted, fontSize: 13, maxWidth: 400, margin: "0 auto 24px", lineHeight: 1.75 }}>
            Our support team responds within 24 hours on business days. Pro subscribers get priority response.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="mailto:admin@skillbite.ai">
              <button className="btn-primary" style={{ padding: "12px 28px" }}>
                📧 Email Support
              </button>
            </a>
           <button className="btn-secondary" style={{ padding: "12px 28px" }} onClick={() => setPage && setPage('community')}>
                  💬 Community Forum
            </button>
          </div>
          <div style={{ fontSize: 11, color: C.textFaint, marginTop: 16 }}>
            admin@skillbite.ai · Response within 24 business hours
          </div>
        </div>
      )}
    </div>
  );
}
